import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  EventNote as EventNoteIcon,
  CalendarToday as CalendarIcon,
  Send as SendIcon,
  Info as InfoIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowIcon,
  RestartAlt as ResetIcon,
  WbSunny as SunIcon,
  Update as UpdateIcon,
  DateRange as DateRangeIcon,
  BeachAccess as BeachIcon,
  Sick as SickIcon,
  Person as PersonIcon,
  School as SchoolIcon,
} from "@mui/icons-material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import { teacherLeaveApply } from "./api/teacherHrApi";
import { TEACHER_LEAVE_TYPES } from "./constants";
import { toUtcMidnightIso } from "./helpers";
import "./teacherHr.css";

/* -------------------- helpers -------------------- */

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d, n) {
  const x = startOfDay(d);
  x.setDate(x.getDate() + n);
  return x;
}

function inclusiveDays(a, b) {
  const A = startOfDay(a).getTime();
  const B = startOfDay(b).getTime();
  return Math.max(0, Math.round((B - A) / (1000 * 60 * 60 * 24)) + 1);
}

function formatDateShort(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateFull(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

/* Icon + color per leave type (safe fallback) */
const LEAVE_META = {
  casual: {
    hex: "#0ea5e9",
    bg: "#e0f2fe",
    icon: <SunIcon fontSize="small" />,
    blurb: "For short personal matters",
  },
  sick: {
    hex: "#ef4444",
    bg: "#fee2e2",
    icon: <SickIcon fontSize="small" />,
    blurb: "For illness or medical reasons",
  },
  earned: {
    hex: "#10b981",
    bg: "#d1fae5",
    icon: <BeachIcon fontSize="small" />,
    blurb: "Accrued paid leave",
  },
  personal: {
    hex: "#8b5cf6",
    bg: "#ede9fe",
    icon: <PersonIcon fontSize="small" />,
    blurb: "For personal matters",
  },
  maternity: {
    hex: "#ec4899",
    bg: "#fce7f3",
    icon: <SchoolIcon fontSize="small" />,
    blurb: "Maternity leave policy",
  },
  duty: {
    hex: "#f59e0b",
    bg: "#fef3c7",
    icon: <SchoolIcon fontSize="small" />,
    blurb: "On-duty / official work",
  },
};

function leaveMeta(type) {
  const key = String(type || "").toLowerCase();
  return (
    LEAVE_META[key] || {
      hex: "#64748b",
      bg: "#e2e8f0",
      icon: <EventNoteIcon fontSize="small" />,
      blurb: "Leave request",
    }
  );
}

/* -------------------- component -------------------- */

export default function LeaveApply() {
  const [leaveType, setLeaveType] = useState(
    TEACHER_LEAVE_TYPES?.[0] || "Casual",
  );
  const [startDate, setStartDate] = useState(() => startOfDay(new Date()));
  const [endDate, setEndDate] = useState(() => startOfDay(new Date()));
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const today = useMemo(() => startOfDay(new Date()), []);

  const durationDays = useMemo(
    () => inclusiveDays(startDate, endDate),
    [startDate, endDate],
  );

  const reasonLen = reason.trim().length;
  const reasonOk = reasonLen >= 5;
  const dateOk = startOfDay(endDate) >= startOfDay(startDate);
  const canSubmit = dateOk && reasonOk && !submitting;

  const selectedMeta = leaveMeta(leaveType);

  const applyShortcut = (start, end) => {
    setStartDate(startOfDay(start));
    setEndDate(startOfDay(end ?? start));
  };

  const onReset = () => {
    setLeaveType(TEACHER_LEAVE_TYPES?.[0] || "Casual");
    setStartDate(today);
    setEndDate(today);
    setReason("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!reasonOk) {
      toast.error("Please provide a reason (at least 5 characters)");
      return;
    }
    if (!dateOk) {
      toast.error("End date cannot be before start date");
      return;
    }
    setSubmitting(true);
    try {
      await teacherLeaveApply({
        leaveType,
        startDate: toUtcMidnightIso(startDate),
        endDate: toUtcMidnightIso(endDate),
        reason: reason.trim(),
      });
      toast.success("Leave application submitted successfully");
      onReset();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Apply failed",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      className="orange-scrollBar"
      sx={{
        maxHeight: 600,
        overflow: "auto",
        p: { xs: 1.5, sm: 2 },
        bgcolor: "#f8fafc",
      }}
    >
      {/* -------------------- Header -------------------- */}
      <Box className="mb-3">
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: "#6366f1",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(99,102,241,0.25)",
            }}
          >
            <EventNoteIcon />
          </Box>
          <Box>
            <Typography
              variant="h6"
              className="fw-bold"
              sx={{ lineHeight: 1.2 }}
            >
              Apply for Leave
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Submit a new leave request — you'll get notified once it's
              approved
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Grid container spacing={2}>
        {/* -------------------- Form Panel (left, wider) -------------------- */}
        <Grid item xs={12} md={8}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid #e5e7eb",
              overflow: "hidden",
            }}
          >
            {/* Card header */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: "1px solid #eef2f7",
                bgcolor: "#fff",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: 1.5,
                      bgcolor: "#eef2ff",
                      color: "#4f46e5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <UpdateIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, fontSize: 14 }}
                  >
                    New Leave Request
                  </Typography>
                </Stack>
                <Tooltip title="Reset form">
                  <IconButton
                    size="small"
                    onClick={onReset}
                    disabled={submitting}
                    sx={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 1.5,
                    }}
                  >
                    <ResetIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            <CardContent sx={{ p: 2.5 }}>
              <Box component="form" onSubmit={onSubmit} noValidate>
                {/* -------------------- Leave type -------------------- */}
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    sx={{ mb: 1 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                      }}
                    >
                      Leave Type
                    </Typography>
                    <Box
                      sx={{
                        px: 0.75,
                        py: 0.1,
                        borderRadius: 999,
                        bgcolor: selectedMeta.bg,
                        color: selectedMeta.hex,
                        fontSize: 10,
                        fontWeight: 700,
                      }}
                    >
                      {leaveType}
                    </Box>
                  </Stack>

                  <Grid container spacing={1}>
                    {TEACHER_LEAVE_TYPES.map((t) => {
                      const meta = leaveMeta(t);
                      const active =
                        String(t).toLowerCase() ===
                        String(leaveType).toLowerCase();
                      return (
                        <Grid item xs={6} sm={4} md={3} key={t}>
                          <Box
                            role="button"
                            tabIndex={0}
                            onClick={() => setLeaveType(t)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setLeaveType(t);
                              }
                            }}
                            sx={{
                              p: 1.25,
                              borderRadius: 2,
                              border: "1.5px solid",
                              borderColor: active ? meta.hex : "#e5e7eb",
                              bgcolor: active ? meta.bg : "#fff",
                              cursor: "pointer",
                              transition: "all .15s ease",
                              "&:hover": {
                                borderColor: meta.hex,
                                transform: "translateY(-1px)",
                                boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
                              },
                              "&:focus-visible": {
                                outline: `2px solid ${meta.hex}55`,
                                outlineOffset: 2,
                              },
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 1.25,
                                  bgcolor: active ? "#fff" : meta.bg,
                                  color: meta.hex,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                {meta.icon}
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    fontSize: 12.5,
                                    fontWeight: 700,
                                    color: active ? meta.hex : "#334155",
                                    lineHeight: 1.2,
                                  }}
                                  noWrap
                                >
                                  {t}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: 10.5,
                                    color: active ? meta.hex : "text.secondary",
                                    opacity: active ? 0.85 : 1,
                                    lineHeight: 1.2,
                                  }}
                                  noWrap
                                >
                                  {meta.blurb}
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>

                <Divider sx={{ my: 2.5 }} />

                {/* -------------------- Date range -------------------- */}
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1.25, flexWrap: "wrap", rowGap: 1 }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <DateRangeIcon
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: "text.secondary",
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                        }}
                      >
                        Date Range
                      </Typography>
                      {durationDays > 0 && (
                        <Box
                          sx={{
                            px: 0.75,
                            py: 0.1,
                            borderRadius: 999,
                            bgcolor: "#eef2ff",
                            color: "#4f46e5",
                            fontSize: 10,
                            fontWeight: 700,
                          }}
                        >
                          {durationDays} day{durationDays > 1 ? "s" : ""}
                        </Box>
                      )}
                    </Stack>

                    {/* Quick shortcuts */}
                    <Stack
                      direction="row"
                      spacing={0.75}
                      sx={{ flexWrap: "wrap", rowGap: 0.75 }}
                    >
                      <Chip
                        size="small"
                        label="Today"
                        onClick={() => applyShortcut(today, today)}
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          bgcolor: "#f1f5f9",
                          color: "#334155",
                          cursor: "pointer",
                          height: 22,
                          "&:hover": { bgcolor: "#e2e8f0" },
                        }}
                      />
                      <Chip
                        size="small"
                        label="Tomorrow"
                        onClick={() =>
                          applyShortcut(addDays(today, 1), addDays(today, 1))
                        }
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          bgcolor: "#f1f5f9",
                          color: "#334155",
                          cursor: "pointer",
                          height: 22,
                          "&:hover": { bgcolor: "#e2e8f0" },
                        }}
                      />
                      <Chip
                        size="small"
                        label="Next 3 days"
                        onClick={() => applyShortcut(today, addDays(today, 2))}
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          bgcolor: "#f1f5f9",
                          color: "#334155",
                          cursor: "pointer",
                          height: 22,
                          "&:hover": { bgcolor: "#e2e8f0" },
                        }}
                      />
                      <Chip
                        size="small"
                        label="Next week"
                        onClick={() => applyShortcut(today, addDays(today, 6))}
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          bgcolor: "#f1f5f9",
                          color: "#334155",
                          cursor: "pointer",
                          height: 22,
                          "&:hover": { bgcolor: "#e2e8f0" },
                        }}
                      />
                    </Stack>
                  </Stack>

                  <Grid container spacing={1.5} alignItems="center">
                    {/* Start date */}
                    <Grid item xs={12} sm={5}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontWeight: 600,
                          display: "block",
                          mb: 0.5,
                          fontSize: 11,
                        }}
                      >
                        Start date
                      </Typography>
                      <DatePicker
                        selected={startDate}
                        onChange={(d) => {
                          if (!d) return;
                          const s = startOfDay(d);
                          setStartDate(s);
                          if (startOfDay(endDate) < s) setEndDate(s);
                        }}
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        minDate={today}
                        dateFormat="dd MMM yyyy"
                        wrapperClassName="d-block w-100"
                        customInput={
                          <Box
                            sx={{
                              border: "1.5px solid #e5e7eb",
                              borderRadius: 2,
                              px: 1.5,
                              py: 1,
                              bgcolor: "#fff",
                              cursor: "pointer",
                              transition: "all .15s ease",
                              "&:hover": {
                                borderColor: "#6366f1",
                                bgcolor: "#fafbff",
                              },
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <CalendarIcon
                              sx={{ fontSize: 18, color: "#6366f1" }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 700,
                                  color: "#0f172a",
                                  lineHeight: 1.2,
                                }}
                                noWrap
                              >
                                {formatDateShort(startDate)}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 10.5,
                                  color: "text.secondary",
                                  lineHeight: 1.2,
                                }}
                                noWrap
                              >
                                {new Date(startDate).toLocaleDateString(
                                  "en-IN",
                                  { weekday: "long" },
                                )}
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </Grid>

                    {/* Arrow */}
                    <Grid
                      item
                      xs={12}
                      sm={2}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        py: { xs: 0, sm: 3 },
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          bgcolor: "#eef2ff",
                          color: "#6366f1",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transform: {
                            xs: "rotate(90deg)",
                            sm: "rotate(0deg)",
                          },
                        }}
                      >
                        <ArrowIcon fontSize="small" />
                      </Box>
                    </Grid>

                    {/* End date */}
                    <Grid item xs={12} sm={5}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontWeight: 600,
                          display: "block",
                          mb: 0.5,
                          fontSize: 11,
                        }}
                      >
                        End date
                      </Typography>
                      <DatePicker
                        selected={endDate}
                        onChange={(d) => d && setEndDate(startOfDay(d))}
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                        dateFormat="dd MMM yyyy"
                        wrapperClassName="d-block w-100"
                        customInput={
                          <Box
                            sx={{
                              border: "1.5px solid #e5e7eb",
                              borderRadius: 2,
                              px: 1.5,
                              py: 1,
                              bgcolor: "#fff",
                              cursor: "pointer",
                              transition: "all .15s ease",
                              "&:hover": {
                                borderColor: "#6366f1",
                                bgcolor: "#fafbff",
                              },
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <CalendarIcon
                              sx={{ fontSize: 18, color: "#6366f1" }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 700,
                                  color: "#0f172a",
                                  lineHeight: 1.2,
                                }}
                                noWrap
                              >
                                {formatDateShort(endDate)}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 10.5,
                                  color: "text.secondary",
                                  lineHeight: 1.2,
                                }}
                                noWrap
                              >
                                {new Date(endDate).toLocaleDateString("en-IN", {
                                  weekday: "long",
                                })}
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </Grid>
                  </Grid>

                  {/* Duration preview banner */}
                  {durationDays > 0 && dateOk && (
                    <Box
                      sx={{
                        mt: 1.5,
                        p: 1.25,
                        borderRadius: 2,
                        bgcolor: "#f0fdf4",
                        border: "1px solid #bbf7d0",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          bgcolor: "#dcfce7",
                          color: "#16a34a",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <AccessTimeIcon fontSize="small" />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: "#166534",
                          fontWeight: 500,
                        }}
                      >
                        You're requesting{" "}
                        <Box component="span" sx={{ fontWeight: 800 }}>
                          {durationDays} day{durationDays > 1 ? "s" : ""}
                        </Box>{" "}
                        of{" "}
                        <Box
                          component="span"
                          sx={{ fontWeight: 800, color: selectedMeta.hex }}
                        >
                          {leaveType}
                        </Box>{" "}
                        leave
                        <Box
                          component="span"
                          sx={{
                            display: { xs: "none", sm: "inline" },
                            ml: 0.5,
                            color: "text.secondary",
                            fontWeight: 400,
                          }}
                        >
                          · {formatDateFull(startDate)}
                          {durationDays > 1 && ` → ${formatDateFull(endDate)}`}
                        </Box>
                      </Typography>
                    </Box>
                  )}

                  {!dateOk && (
                    <Box
                      sx={{
                        mt: 1.5,
                        p: 1.25,
                        borderRadius: 2,
                        bgcolor: "#fef2f2",
                        border: "1px solid #fecaca",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                      }}
                    >
                      <InfoIcon sx={{ color: "#ef4444", fontSize: 18 }} />
                      <Typography
                        sx={{
                          fontSize: 12.5,
                          color: "#b91c1c",
                          fontWeight: 600,
                        }}
                      >
                        End date cannot be before start date.
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Divider sx={{ my: 2.5 }} />

                {/* -------------------- Reason -------------------- */}
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                      }}
                    >
                      Reason <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: reasonOk ? "#10b981" : "text.secondary",
                        fontSize: 11,
                      }}
                    >
                      {reasonLen} / 500
                    </Typography>
                  </Stack>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={8}
                    value={reason}
                    onChange={(e) => setReason(e.target.value.slice(0, 500))}
                    placeholder={`Describe why you need ${leaveType.toLowerCase()} leave…`}
                    variant="outlined"
                    error={reason.length > 0 && !reasonOk}
                    helperText={
                      reason.length > 0 && !reasonOk
                        ? "Please write at least 5 characters"
                        : "Be specific — this helps your admin approve faster"
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        fontSize: 13.5,
                      },
                    }}
                  />
                </Box>

                {/* -------------------- Actions -------------------- */}
                <Divider sx={{ mb: 2 }} />
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontSize: 11.5 }}
                  >
                    <Box component="span" sx={{ color: "#ef4444" }}>
                      *
                    </Box>{" "}
                    All fields are required. You can withdraw pending requests
                    from your history.
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent={{ xs: "flex-end", sm: "flex-start" }}
                  >
                    <Button
                      variant="outlined"
                      onClick={onReset}
                      disabled={submitting}
                      startIcon={<ResetIcon fontSize="small" />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: 2,
                        borderColor: "#e5e7eb",
                        color: "#334155",
                        "&:hover": {
                          borderColor: "#cbd5e1",
                          bgcolor: "#f8fafc",
                        },
                      }}
                    >
                      Reset
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={!canSubmit}
                      startIcon={submitting ? null : <SendIcon />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: 2,
                        px: 3,
                        bgcolor: "#6366f1",
                        boxShadow: "none",
                        "&:hover": { bgcolor: "#4f46e5" },
                        "&.Mui-disabled": {
                          bgcolor: "#e2e8f0",
                          color: "#94a3b8",
                        },
                      }}
                    >
                      {submitting ? "Submitting…" : "Submit Application"}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* -------------------- Sidebar (right, narrower) -------------------- */}
        <Grid item xs={12} md={4}>
          <Stack
            spacing={2}
            sx={{ position: { md: "sticky" }, top: { md: 8 } }}
          >
            {/* Guidelines */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: "1px solid #eef2f7",
                  bgcolor: "#f0f9ff",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: 1.5,
                      bgcolor: "#e0f2fe",
                      color: "#0369a1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <InfoIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, fontSize: 13.5 }}
                  >
                    Available Leave Types
                  </Typography>
                </Stack>
              </Box>
              <CardContent sx={{ p: 2 }}>
                <Stack spacing={1.25}>
                  {TEACHER_LEAVE_TYPES.map((type) => {
                    const meta = leaveMeta(type);
                    return (
                      <Box
                        key={type}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          p: 1,
                          borderRadius: 1.5,
                          border: "1px solid #eef2f7",
                          bgcolor: "#fafbfc",
                        }}
                      >
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: 1.5,
                            bgcolor: meta.bg,
                            color: meta.hex,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {meta.icon}
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: 12.5,
                              fontWeight: 700,
                              color: "#0f172a",
                              lineHeight: 1.2,
                            }}
                          >
                            {type}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 10.5,
                              color: "text.secondary",
                              lineHeight: 1.2,
                            }}
                          >
                            {meta.blurb}
                          </Typography>
                        </Box>
                        <CheckCircleIcon
                          sx={{ fontSize: 16, color: meta.hex, flexShrink: 0 }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>

            {/* What happens next */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                bgcolor: "#fffbeb",
                overflow: "hidden",
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 1.5 }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1.5,
                      bgcolor: "#fef3c7",
                      color: "#b45309",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <InfoIcon sx={{ fontSize: 16 }} />
                  </Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, fontSize: 13, color: "#92400e" }}
                  >
                    What happens next?
                  </Typography>
                </Stack>
                <Stack spacing={1.25}>
                  {[
                    { n: 1, text: "Your request is sent to the school admin" },
                    { n: 2, text: "You'll get notified once approved" },
                    { n: 3, text: "Track status in your leave history" },
                  ].map((s) => (
                    <Stack
                      key={s.n}
                      direction="row"
                      alignItems="flex-start"
                      spacing={1}
                    >
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          bgcolor: "#fde68a",
                          color: "#92400e",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 11,
                          fontWeight: 800,
                          flexShrink: 0,
                          mt: 0.25,
                        }}
                      >
                        {s.n}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 12.5,
                          color: "#78350f",
                          lineHeight: 1.4,
                        }}
                      >
                        {s.text}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
